<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import PortalJoinButton from './PortalJoinButton.vue';
import PortalSessionFiles from './PortalSessionFiles.vue';
import PortalSessionSpeakers from './PortalSessionSpeakers.vue';
import { mySession } from '../../api/portal';
import { writeSeat } from '../../helpers/sessionSeat';
import { speakingLook } from '../../helpers/speakerConsent';
import { seatOfferLine, seatSavedLine, timeState } from '../../helpers/sessionTimes';
import { reportError } from '../../faro';

/**
 * A session's details, opened in place under its row (XN-4745): the
 * description, What's covered, every speaker, the files, then Join and Save
 * my seat. Asked once, when first opened; the row keeps it mounted after
 * that. The actions read the list's entry, so they show while the details
 * load. The seat covers the whole series, as the list's always has; the line
 * under it says a seat is not booking, and when the reminder email comes.
 */
const props = defineProps({
  session: { type: Object, required: true },
  now: { type: Date, required: true },
  timeZone: { type: String, required: true },
  joining: { type: Boolean, default: false },
});
const emit = defineEmits(['seat', 'gone', 'join']);
const router = useRouter();

const state = ref('loading'); // loading | ready | error
const details = ref(null);
async function load() {
  state.value = 'loading';
  try {
    details.value = await mySession(props.session.id);
    state.value = 'ready';
  } catch (error) {
    if (error?.response?.status === 404) {
      window.loadNotification?.({ message: 'That session is no longer available.', error: true });
      emit('gone', props.session.id);
      return;
    }
    reportError(error);
    state.value = 'error';
  }
}
onMounted(load);

// Speakers and Files side by side; with only one of them it takes the width.
const hasPeople = computed(() => Boolean(details.value?.host?.name || details.value?.speakers?.some((person) => person?.name)));
const hasFiles = computed(() => Boolean(details.value?.handouts?.length));
const dropFile = (id) => { details.value = { ...details.value, handouts: details.value.handouts.filter((file) => file.id !== id) }; };
// The last file took Files, and focus, with it: Join (or the invitation) is always there to take it.
const actions = ref(null);
const focusActions = () => actions.value?.querySelector('button')?.focus();

const time = computed(() => timeState(props.session, props.now));
const speak = computed(() => speakingLook(props.session.speaking));
const openInvitation = () => router.push(props.session.speaking?.invitation_path ?? `/hub/sessions/${props.session.id}/speaking`);
const seated = computed(() => !speak.value && !time.value.joinable);

// The seat, as the list holds it: shown at once, put back on failure.
const busy = ref(false);
const seat = ref(Boolean(props.session.seat));
// `session.seat` is this occurrence's only. The series wording needs the whole
// series seated: this panel's own series-wide save, or every date still to come
// in the loaded details carrying a seat.
const ownSeries = ref(false);
watch(() => props.session.seat, (value) => {
  if (busy.value || Boolean(value) === seat.value) return;
  seat.value = Boolean(value); // saved or released elsewhere: our own write no longer speaks for the series
  ownSeries.value = false;
});
const wholeSeries = computed(() => {
  if (!props.session.series) return false;
  if (ownSeries.value) return true;
  const coming = (details.value?.series?.dates ?? []).filter((date) => date.state === 'next' || date.state === 'upcoming');
  return coming.length > 0 && coming.every((date) => (date.id === props.session.id ? seat.value : date.seat));
});
const line = computed(() => (seat.value
  ? seatSavedLine(wholeSeries.value ? props.session : { ...props.session, series: null }, props.now, props.timeZone)
  : seatOfferLine(props.session, props.now, props.timeZone)));

async function toggleSeat() {
  if (busy.value) return;
  const on = !seat.value;
  busy.value = true;
  seat.value = on;
  const outcome = await writeSeat(props.session.id, on, { series: Boolean(props.session.series) });
  busy.value = false;
  if (outcome === 'saved' || outcome === 'released') {
    ownSeries.value = on && Boolean(props.session.series);
    emit('seat', { id: props.session.id, seat: on, seriesId: props.session.series?.id ?? null });
    return;
  }
  seat.value = !on;
  if (outcome === 'gone') emit('gone', props.session.id);
}
</script>

<template>
  <div class="panel" data-testid="session-panel">
    <div v-if="state === 'loading'" class="panel__loading" aria-busy="true">
      <XlSkeleton variant="text" :lines="3" />
    </div>

    <div v-else-if="state === 'error'" class="panel__error" role="alert">
      <p>This session's details couldn't be loaded.</p>
      <XlButton variant="ghost" size="sm" data-testid="panel-retry" @click="load">Try again</XlButton>
    </div>

    <template v-else>
      <p v-if="details.description" class="panel__about" data-testid="panel-description">{{ details.description }}</p>
      <section v-if="details.points?.length" class="panel__covered">
        <h3 class="panel__h">What's covered</h3>
        <ul class="panel__list"><li v-for="point in details.points" :key="point" data-testid="point">{{ point }}</li></ul>
      </section>
      <div v-if="hasPeople || hasFiles" class="panel__cols" :class="{ 'panel__cols--one': !(hasPeople && hasFiles) }">
        <PortalSessionSpeakers :host="details.host" :speakers="details.speakers ?? []" />
        <PortalSessionFiles :files="details.handouts ?? []" @removed="dropFile" @emptied="focusActions" />
      </div>
    </template>

    <div ref="actions" class="panel__actions">
      <XlButton v-if="speak?.asked" variant="filled" size="sm" data-testid="read-invitation" @click="openInvitation">
        Read the invitation<span class="panel__sr">: {{ session.title }}</span>
      </XlButton>
      <template v-else>
        <PortalJoinButton
          :speaker="Boolean(speak?.speaking)"
          :label="speak?.speaking ? 'Join as a speaker' : 'Join session'"
          :title="session.title"
          :joinable="time.joinable"
          :loading="joining"
          @join="emit('join')"
        />
        <XlButton
          v-if="seated"
          :variant="seat ? 'ghost' : 'filled'"
          size="sm"
          data-testid="seat-toggle"
          @click="toggleSeat"
        >{{ seat ? 'Seat saved' : 'Save my seat' }}<span class="panel__sr">: {{ session.title }}</span></XlButton>
      </template>
    </div>
    <p v-if="seated" class="panel__note" aria-live="polite" data-testid="seat-note">{{ line }}</p>
  </div>
</template>

<style scoped>
/* Prototype cpRow's open block: a hairline under the row, the paper tone, and the
   row's own 18px edge, so the panel lines up with the date chip. */
.panel {
  container-type: inline-size; padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 18px;
  border-top: 1px solid var(--xl-color-border-subtle); border-radius: 0 0 17px 17px; background: var(--xl-color-background);
}
.panel__loading { padding-top: 6px; }
.panel__error {
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  padding: 14px 16px; border-radius: 14px; font-size: 14px;
  background: var(--xl-color-error-container, #FBEAEA); border: 1px solid var(--xl-color-error); color: var(--xl-color-error);
}
.panel__error p { margin: 0; }
.panel__about { margin: 0; max-width: 62ch; font-size: 15px; line-height: 1.65; color: var(--xl-color-on-surface-variant); }
.panel__h { margin: 0 0 8px; font-size: 13px; font-weight: 700; color: var(--xl-color-on-surface); }
.panel__list { margin: 0; padding-left: 20px; font-size: 14.5px; line-height: 1.8; color: var(--xl-color-on-surface-variant); }
/* Speakers beside Files; with no files, Speakers takes the width. */
.panel__cols { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; align-items: start; }
.panel__cols--one { grid-template-columns: minmax(0, 1fr); }
.panel__actions { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.panel__note { margin: -8px 0 0; font-size: 13px; color: var(--xl-color-secondary-text); }
.panel__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

@container (max-width: 560px) {
  .panel__cols { grid-template-columns: minmax(0, 1fr); }
}
</style>
